<template>
  <div class="resend-confirm-code">
    <auth-container writerType="imps">
      <template v-if="!inputConfirmCode">
        <h1>{{ $t("resendConfirmCodePage.title") }}</h1>
        <p class="description">{{ $t("resendConfirmCodePage.description") }}</p>
        <div class="message" v-if="message" v-html="message"></div>
        <text-field
          class="input"
          type="email"
          icon="email"
          :label="$t('resendConfirmCodePage.emailLabel')"
          :placeholder="$t('resendConfirmCodePage.emailPlaceholder')"
          v-model="email"
        />
        <div class="button-container">
          <button-default class="submit-button" @click="resend" :disabled="!email">
            {{ $t("resendConfirmCodePage.submitButton") }}
          </button-default>
        </div>
        <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
      </template>
      <sign-up-confirm-code v-else :email="email" @complete="complete" />
    </auth-container>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import TextField from "@/components/molecules/TextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import AuthContainer from "@/components/organisms/AuthContainer.vue";
import SignUpConfirmCode from "@/components/organisms/SignUpConfirmCode.vue";
import { Auth } from "aws-amplify";
import { resolveSafeRedirect, withRedirect } from "@/lib/utils/authRedirect";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { TextField, ButtonDefault, AuthContainer, SignUpConfirmCode },
  props: {
    propEmail: {
      type: String,
      default: "",
    },
    propMessage: {
      type: String,
      default: "",
    },
  },
  name: "signup",
  created() {
    const { propEmail, propMessage } = this;

    this.email = propEmail;
    this.message = propMessage;
  },
  data() {
    return {
      confirmCode: "",
      email: "",
      message: "",
      errorMessage: "",
      inputConfirmCode: false,
    };
  },
  methods: {
    async complete() {
      const { email } = this;
      this.$router.push(
        withRedirect({ name: "signIn", params: { email } }, resolveSafeRedirect(this.$route.query.redirect))
      );
    },
    async resend() {
      const { email } = this;

      // リクエスト開始時にメッセージをクリア
      this.message = "";
      this.errorMessage = "";

      // バリデーション
      if (!email) {
        this.errorMessage = this.$t("resendConfirmCodePage.validation.emailRequired") as string;
        return;
      }
      // eslint-disable-next-line no-useless-escape
      if (!email.match(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/)) {
        this.errorMessage = this.$t("resendConfirmCodePage.validation.invalidEmailFormat") as string;
        return;
      }

      try {
        await Auth.resendSignUp(email);
        this.message = this.$t("resendConfirmCodePage.validation.codeSent") as string;
        this.confirmCode = "";
        this.inputConfirmCode = true;
      } catch (err) {
        const error = err as any;
        if (error.code === "InvalidParameterException") {
          this.errorMessage = this.$t("resendConfirmCodePage.validation.userAlreadyConfirmed") as string;
        } else if (error.code === "UserNotFoundException") {
          this.errorMessage = this.$t("resendConfirmCodePage.validation.userNotFound") as string;
        } else if (error.code === "LimitExceededException") {
          this.errorMessage = this.$t("resendConfirmCodePage.validation.limitExceeded") as string;
        } else {
          this.errorMessage = `${this.$t("resendConfirmCodePage.validation.genericError")} ${JSON.stringify(error)}`;
        }
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.resend-confirm-code {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
}

h1 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--color-text-normal);
  margin-bottom: 12px;
}

.description {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
  margin-bottom: 15px;
}

.input {
  margin-bottom: 15px;
  --text-field-bg: var(--color-surface-base);
  --text-field-border: none;
  --text-field-icon-color: var(--color-text-low);
}

.message {
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.error-message {
  color: var(--color-warning);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;

  .submit-button {
    margin: 0;
    width: 100%;
    border-radius: var(--radius-rounded);
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    cursor: pointer;
  }
}
</style>
